import React from "react";
import FormProvider, { useFormProvider } from "../form-provider";
import slugify from "slugify";
import { navigate } from "gatsby";
import { useFormStepProvider } from "../form-step-provider";
import { useDropzone } from 'react-dropzone';
import { useLocation } from "@reach/router";
import * as qs from 'qs';
var FormFieldType;
(function (FormFieldType) {
    FormFieldType["select"] = "select";
    FormFieldType["input"] = "input";
    FormFieldType["email"] = "email";
    FormFieldType["tel"] = "tel";
    FormFieldType["number"] = "number";
    FormFieldType["textarea"] = "textarea";
    FormFieldType["checkbox"] = "checkbox";
    FormFieldType["checkboxes"] = "checkboxes";
    FormFieldType["radio"] = "radio";
    FormFieldType["file"] = "file";
    FormFieldType["hidden"] = "hidden";
})(FormFieldType || (FormFieldType = {}));
const typeRules = {
    password: /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
    email: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    tel: /[0-9 \-\(\)]+/g,
    number: /^-?\d+\.?\d*$/,
    text: undefined,
};
const FieldRenderer = ({ field, renderField, schema, loading, index, loadingFiles, setLoadingFiles }) => {
    const { search } = useLocation();
    const [parsed, setParsed] = React.useState(qs.parse(search.replace("?", "")));
    const [successfulUpload, setSuccessfulUpload] = React.useState(false);
    const [file, setFile] = React.useState(null);
    const fieldName = slugify(field.label, { lower: true, strict: true });
    const possibleDefaultValue = field.prefill ? parsed?.[field.prefill] : "";
    const provider = useFormProvider();
    const typename = field.__typename.replace("DatoCms", "");
    const { getInputProps } = useDropzone({
        multiple: false,
        disabled: loading,
        onDrop: (droppedFiles) => {
            if (loadingFiles.includes(field.id))
                return;
            const file = droppedFiles[0];
            const fileReader = new FileReader();
            provider.clearErrors(fieldName);
            setFile(file);
            setLoadingFiles((e) => [...e, field.id]);
            setSuccessfulUpload(false);
            fileReader.onloadend = (evt) => {
                // 2. Grab the first 600 bytes of the file for mime type analysis server side - most mime
                // types use the first few bytes, but some (looking at you, Microsoft...) start at the
                // 513th byte; ISO CD files start at the 32,770th byte, so we'll ignore those rather than
                // send that much data for each file - users of this system certainly aren't expected to
                // upload ISO CD files! Also, some .zip files may start their identifying bytes at the
                // 29,153nd byte - we ignore those too (mostly, .zip files start at the first, 31st, or 527th byte).
                //const blob = file.data.slice(0, 600);
                // 4. Once the filereader has successfully finished reading the file...
                if (evt.target.readyState === FileReader.DONE) {
                    // Get the unsigned 8 bit int8Array (ie Uint8Array) of the 600 bytes (this looks like '[119,80,78,71...]'):
                    const uint = new Uint8Array(evt.target.result.slice(0, 600));
                    fetch(`/api/gatsby-plugin-atollon/file`, {
                        method: 'POST',
                        body: JSON.stringify({
                            id: formOriginalId,
                            fieldId: field.originalId,
                            bytes: uint,
                            fullFileName: file.name,
                        })
                    }).then((res) => res.json()).then((res) => {
                        if (res.reason) {
                            provider.setError(fieldName, {
                                type: 'pattern',
                                message: res.reason
                            });
                            setLoadingFiles((e) => [...e.filter(y => y !== field.id)]);
                            setFile(null);
                            setSuccessfulUpload(false);
                        }
                        else {
                            const { url, fields } = res.meta;
                            const fd = new FormData();
                            Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
                            fd.append('file', file);
                            fetch(url, {
                                method: 'POST',
                                headers: {
                                //'Content-Type': 'multipart/form-data',
                                },
                                body: fd,
                            }).then((res) => {
                                if (res.ok) {
                                    provider.setValue(fieldName, fields.key, { shouldValidate: true });
                                    provider.clearErrors(fieldName);
                                    setSuccessfulUpload(true);
                                }
                                else {
                                    provider.setError(fieldName, {
                                        type: 'pattern',
                                        message: 'File could not be uploaded'
                                    });
                                    setSuccessfulUpload(false);
                                    setFile(null);
                                }
                                setLoadingFiles((e) => [...e.filter(y => y !== field.id)]);
                            });
                        }
                    });
                }
            };
            // 3. Start reading those 600 bytes...continues above at 'filereader.onloadend':
            fileReader.readAsArrayBuffer(file);
        }
    });
    const formOriginalId = schema.originalId;
    const hookRegistration = provider.register(fieldName, {
        required: field.required,
        pattern: (() => {
            if (typename === "Input") {
                if (field.inputType === "email") {
                    return typeRules.email;
                }
                if (field.inputType === "tel") {
                    return typeRules.tel;
                }
            }
            return undefined;
        })(),
    });
    React.useEffect(() => {
        if (field?.prefill) {
            provider.setValue(fieldName, possibleDefaultValue);
        }
    }, [possibleDefaultValue]);
    const registration = typename === "FileUpload" ? getInputProps() : hookRegistration;
    if (typename === "Hidden") {
        return (React.createElement("input", { type: "hidden", ...registration }));
    }
    return renderField({
        index: index,
        id: `form-${schema.originalId}-${slugify(field.label, { lower: true, strict: true })}`,
        label: field.label,
        placeholder: field.placeholder,
        disabled: loading,
        hasErrors: !!(provider.errors && provider.errors[slugify(field.label, { lower: true, strict: true })]),
        errors: (provider.errors && provider.errors[slugify(field.label, { lower: true, strict: true })]) ? [provider.errors[slugify(field.label, { lower: true, strict: true })]].map(x => {
            if (x.type === 'required') {
                return 'This is a required field.';
            }
            if (x.type === 'pattern') {
                if (field.inputType === 'email') {
                    return 'This is not a valid email.';
                }
                if (field.inputType === 'tel') {
                    return 'This is not a valid phone number.';
                }
                if (typename === 'FileUpload') {
                    return x.message;
                }
                return 'This is not valid.';
            }
            if (x.type === 'maxLength') {
                return 'Maximum length exceeded.';
            }
            if (x.type === 'minLength') {
                return 'This is not long enough.';
            }
        }) : [],
        invalid: !!(provider.errors && provider.errors[slugify(field.label, { lower: true, strict: true })]),
        options: (field.options || []).map(x => ({ label: x.option, value: x.option, id: `field-${schema.originalId}-${index}-${slugify(x.option, { lower: true, strict: true })}` })),
        required: field.required,
        isUploadingFile: loadingFiles.includes(field.id),
        fileUploadSuccessful: !loadingFiles.includes(field.id) && successfulUpload,
        type: (() => {
            if (typename === "FileUpload") {
                return "file";
            }
            if (typename === "Input") {
                return field.inputType.toLowerCase();
            }
            if (typename === "Select") {
                return field.display.toLowerCase();
            }
            if (typename === "CheckboxGroup") {
                return "checkboxes";
            }
            return typename.toLowerCase();
        })(),
        autoComplete: field.autocomplete,
        files: file ? [file] : null,
        ...registration,
    }, provider.getValues());
};
const Form = ({ schema, values, renderField, renderSubmitButton, renderLoadingSpinner, renderConfirmationMessage, renderApiErrors, onPreSubmit, onSubmitting, onSubmitSuccess, onSubmitError, }) => {
    const [loading, setLoading] = React.useState(false);
    const [loadingFiles, setLoadingFiles] = React.useState([]);
    const [apiErrors, setApiErrors] = React.useState([]);
    const [confirmationMessage, setConfirmationMessage] = React.useState(null);
    const stepContext = useFormStepProvider();
    if (!schema.originalId) {
        throw new Error("Missing originalId on Form");
    }
    if (!renderField) {
        throw new Error("renderField must be defined on Form");
    }
    if (!renderSubmitButton) {
        throw new Error("renderSubmitButton must be defined on Form");
    }
    if (!renderApiErrors) {
        throw new Error("renderApiErrors must be defined on Form");
    }
    if (!renderLoadingSpinner) {
        throw new Error("renderLoadingSpinner must be defined on Form");
    }
    const onSubmit = (rawData) => {
        setLoading(true);
        setApiErrors([]);
        if (onSubmitting) {
            onSubmitting();
        }
        let formData = onPreSubmit ? onPreSubmit(rawData) : rawData;
        fetch(`/${process.env.NODE_ENV === "development" ? "api/gatsby-plugin-atollon/go" : "go"}?id=${schema.originalId}`, {
            method: "POST",
            body: JSON.stringify({
                data: formData,
                steps: stepContext?.steps || []
            })
        })
            .then((res) => {
            return new Promise((resolve, reject) => {
                res.text().then((e) => {
                    if (!res.ok) {
                        if (e.includes("{")) {
                            const json = JSON.parse(e);
                            console.error(json.message, json.reason);
                            reject({ message: json.message });
                        }
                        else {
                            const message = 'An unknown error occurred.';
                            console.log(message, res.statusText);
                            reject({ message });
                        }
                    }
                    else {
                        resolve(JSON.parse(e));
                    }
                });
            });
        })
            .then((res) => {
            if (onSubmitSuccess) {
                onSubmitSuccess(res);
            }
            if (res.meta) {
                if (res.meta.loginCallback) {
                    const loginField = rawData[res.meta.loginCallback];
                    //signIn("email", { email: loginField, redirect: false })
                }
                if (res.meta.redirectTo && res.meta.confirmationMessage) {
                    setConfirmationMessage(res.meta.confirmationMessage);
                    setTimeout(() => {
                        navigate(res.meta.redirectTo);
                    }, 4000);
                }
                else if (res.meta.redirectTo) {
                    navigate(res.meta.redirectTo);
                }
                else if (res.meta.confirmationMessage) {
                    setConfirmationMessage(res.meta.confirmationMessage);
                }
            }
        })
            .catch((err) => {
            setApiErrors([err.message]);
            if (onSubmitError) {
                onSubmitError(err);
            }
        })
            .finally(() => {
            setLoading(false);
        });
    };
    return (React.createElement(FormProvider, { values: values, disabled: loading, onSubmit: onSubmit },
        schema.formFields.map((field, idx) => (React.createElement(FieldRenderer, { key: field.id, field: field, renderField: renderField, schema: schema, loading: loading || loadingFiles.includes(field.id), index: idx, loadingFiles: loadingFiles, setLoadingFiles: setLoadingFiles }))),
        renderSubmitButton({
            type: 'submit',
            children: (loading || loadingFiles.length > 0) ? renderLoadingSpinner() : (schema.submitButtonText || 'Submit'),
            disabled: loading || loadingFiles.length > 0
        }),
        apiErrors && apiErrors.length > 0 && renderApiErrors({
            errors: apiErrors
        }),
        confirmationMessage && renderConfirmationMessage({
            message: confirmationMessage
        })));
};
export default Form;
//# sourceMappingURL=index.js.map